import { useRef, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import * as _ from "lodash";

import { ChartFilters, DataDescriptorEntity } from "@ctra/api";
import { Nullable } from "@ctra/utils";

export type FilterSetter = <A extends keyof ChartFilters>(
  variantID: DataDescriptorEntity["id"],
  filterName: A,
  value: ChartFilters[A]
) => void;

/**
 * Merge two filter objects without undefined values
 * @param {Nullable<ChartFilters>} a
 * @param {Nullable<ChartFilters>} b
 * @returns {ChartFilters}
 */
const merge = (
  a: Record<DataDescriptorEntity["id"], Nullable<ChartFilters>>,
  b: Record<DataDescriptorEntity["id"], Nullable<ChartFilters>>
): Record<DataDescriptorEntity["id"], ChartFilters> => {
  return _.mergeWith(
    _.cloneDeep(a),
    b,
    (objValue: Nullable<ChartFilters>, srcValue: Nullable<ChartFilters>) => {
      return _.omitBy(
        _.mapValues(
          {
            ...objValue,
            ...srcValue
          },
          _.compact
        ),
        _.isEmpty
      ) as ChartFilters;
    }
  ) as Record<DataDescriptorEntity["id"], ChartFilters>;
};

/**
 * API hook to to update chart filters
 * @param defaultFilters - these come from the chart object, initially empty
 * @param presetFilters - developer-presets
 */
export const useFilter = (
  defaultFilters: Record<DataDescriptorEntity["id"], Nullable<ChartFilters>>,
  presetFilters: Record<DataDescriptorEntity["id"], ChartFilters> = {}
): [Record<DataDescriptorEntity["id"], ChartFilters>, FilterSetter, () => void] => {
  /**
   * We combine the preset and the user defined filters.
   * The preset always have priority. Eg. via props we can set a
   * list of farm ids which will apply to the chart and can be overridden
   * by the user interface. The default filters come from the BE
   * baked in the chart config or set by Redux.
   *
   * Using a ref keeps track of the incoming filters to
   * so changes from the outside may be detected.
   * If the props of the wrapper component change,
   * the filters must be updated.
   *
   * TODO I am unsure if this is the best way to handle this. A state should be enough.
   */
  const filterRef = useRef<Record<DataDescriptorEntity["id"], ChartFilters>>(
    merge(defaultFilters, presetFilters)
  );

  const [filters, setFilters] = useState<Record<DataDescriptorEntity["id"], ChartFilters>>(filterRef.current);

  useDeepCompareEffect(() => {
    const updatedFilters = merge(defaultFilters, presetFilters);

    /**
     * Prevent a setState loop by checking
     * if the filters actually got updated from the outside.
     */
    if (!_.isEqual(updatedFilters, filterRef.current)) {
      setFilters(updatedFilters);
      filterRef.current = updatedFilters;
    }
  }, [defaultFilters, presetFilters]);

  /**
   * Update a given filter with the provided value
   * @param {DataDescriptorEntity["id"]} variantID
   * @param {A} filterName
   * @param {ChartFilters[A]} value
   */
  const updateFilter: FilterSetter = (variantID, filterName, value) => {
    setFilters((filters) => {
      return merge(filters, {
        [variantID]: {
          [filterName]: value
        }
      });
    });
  };

  /**
   * Reset the filters to the initial state
   */
  const reset = () => {
    setFilters(merge(defaultFilters, presetFilters));
  };

  return [filters, updateFilter, reset];
};
